import { AfterViewInit, Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { createForm, FormType, subformComponentProviders } from 'ngx-sub-form';
import { User } from 'src/app/core/components/user-admin-page/user-model';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { unitsLib } from 'src/app/core/services/unit-library';
import { Choke } from 'src/app/perical/models/chokes.model';
import { Flow } from 'src/app/perical/models/flow.model';
import { Fluid, FluidType } from 'src/app/perical/models/fluid.model';
import { Perforation } from 'src/app/perical/models/perforation.model';
import { PericalOptions } from 'src/app/perical/models/pericalOptions';
import { Reservoir } from 'src/app/perical/models/reservoirPerformance';
import { InjectionOperation, OperationType } from 'src/app/perical/models/thermal-operation.model';
import { GetShortLengthValueFromInches } from 'src/app/perivis/shared/helpers/units.helper';

export interface InjectionOperationForm {
  type: OperationType;
  wellheadPressure: number;
  inletTemperature: number;
  perforation: Perforation;
  fluid: Fluid;
  standardFluidFlow: Flow;
  hydrocarbonFlow: Flow;
  pressureLocation: string;
  time: number;
  pressure: number;
  location: string;
  enthalpyOptionsSource: any;
  nodalAnalysis: any;
  customEnthalpyOptions: any;
  includeRiser: boolean;
  outsideDiameter: number;
  insideDiameter: number;
  annulusIndex: number;
}

@Component({
  selector: 'app-injection-operation-details',
  templateUrl: './injection-operation-details.component.html',
  standalone: false,
  providers: subformComponentProviders(InjectionOperationDetailsComponent)
})
export class InjectionOperationDetailsComponent implements OnChanges, AfterViewInit {

  constructor() {
    this.timeUnits = [
      { name: 'Minutes', value: 'DurationUnit.Minute' },
      { name: 'Hours', value: 'DurationUnit.Hour' },
      { name: 'Days', value: 'DurationUnit.Day' },
      { name: 'Months', value: 'DurationUnit.Month30' },
      { name: 'Years', value: 'DurationUnit.Year365' }
    ];
  }

  @Input()
  public user: User;

  @Input()
  public isReadOnly: boolean;

  @Input()
  public userUnits: UserUnitsModel;

  @Input()
  public reservoirs: Reservoir[];

  @Input()
  public currentTubularIndex: number;

  @Input()
  public isSubsea: boolean;

  @Input()
  public chokes: Choke[];

  @Input()
  public drillString: any;

  @Input()
  public showRestartOperation: boolean;

  @Input()
  public pericalOptions: PericalOptions;

  @Input()
  public showLandingString: boolean;

  @Input()
  public defaultRiser: any;

  public fluids: Fluid[];
  @Input('fluids') set _fluids(fluids: Fluid[]) {
    this.fluids = fluids?.sort((a, b) => a.state.name.localeCompare(b.state.name));
  }

  public perforations: Perforation[];
  @Input('perforations') set _perforations(perforations: Perforation[]) {
    this.perforations = perforations;
  }

  @Input()
  public surfaceTemperature: number;

  @Output()
  fluidChanged = new EventEmitter<{ hasGasProperty: boolean, hasOilProperty: boolean, hasWaterMudProperty: boolean }>();

  @Output()
  selectedTabChange = new EventEmitter<string>();

  public isShutInCheck: boolean;
  public shutinTimeDisabled: boolean;
  public timeUnits: any[];
  public FluidType: typeof FluidType = FluidType;
  public tempUnit: string;
  public pipeRoughnessUnit: string;
  public pressureUnit: string;
  public temperatureValidation: { min: number, max: number };
  public pressureValidation: { min: number, max: number };
  public showNodal = false;
  public includeNodalAnalysis = false;
  public includeRestartOperation = false;
  public enthalpyOptionsSource: string | null = null;
  public enthalpyOptions: any;
  public showRiser: boolean;
  public selectedLandingString: string;
  public landingStringMissing: boolean;

  private _dataBackup: any | null = null;

  public pressureLocations = [
    { name: 'Wellhead', value: 'wellhead' },
    { name: 'Perforations', value: 'perforation' }
  ];

  public form = createForm<InjectionOperation, InjectionOperationForm>(this, {
    formType: FormType.SUB,
    formControls: {
      type: new UntypedFormControl(OperationType.INJECTION),
      fluid: new UntypedFormControl(null, [Validators.required]),
      wellheadPressure: new UntypedFormControl(null, { updateOn: 'blur' }),
      inletTemperature: new UntypedFormControl(null, { updateOn: 'blur' }),
      perforation: new UntypedFormControl(null, [Validators.required]),
      standardFluidFlow: new UntypedFormControl(null),
      hydrocarbonFlow: new UntypedFormControl(null),
      pressureLocation: new UntypedFormControl('perforation', [Validators.required]),
      time: new UntypedFormControl(0, {updateOn: 'blur', validators: [Validators.required, Validators.min(0), Validators.max(100000)] }),
      pressure: new UntypedFormControl(0, { updateOn: 'blur' }),
      location: new UntypedFormControl('wellhead'),
      enthalpyOptionsSource: new UntypedFormControl('internalDefaults'),
      nodalAnalysis: new UntypedFormControl(null),
      customEnthalpyOptions: new UntypedFormControl(null),
      includeRiser: new UntypedFormControl(null),
      outsideDiameter: new UntypedFormControl(null, { updateOn: 'blur' }),
      insideDiameter: new UntypedFormControl(null, { updateOn: 'blur' }),
      annulusIndex: new UntypedFormControl(null)
    },
    toFormGroup: (obj: InjectionOperation | null): InjectionOperationForm | null => {
      if (!obj) {
        return null;
      }

      this._dataBackup = { ...obj };

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { fluid, perforation, flow, includeRiser, riser, shutIn, ...commonValues } = obj;

      obj.fluid = this.fluids?.find(f => f.id === obj.fluid.id);
      this.showRiser = includeRiser;

      this.optionsCheck(obj.fluid.id);

      this.includeNodalAnalysis = obj.nodalAnalysis != null;
      this.includeRestartOperation = obj.nodalAnalysis?.includeRestartOperation == true ? true : false;
      this.enableDisableNodalAnalysis();
      // this.checkIsShutin(shutIn.time);

      return {
        standardFluidFlow: obj.fluid.state?.type === FluidType.BRINE || obj.fluid.state?.type === FluidType.STANDARDMUD || obj.fluid.state?.type === FluidType.ADVANCEDMUD || obj.fluid.state?.type === FluidType.CEMENTSLURRY || obj.fluid.state?.type === FluidType.POLYMER ? obj.flow : null,
        hydrocarbonFlow: obj.fluid.state?.type === FluidType.VLE || obj.fluid.state?.type === FluidType.BLACKOIL || obj.fluid.state?.type === FluidType.HYDROCARBON || obj.fluid.state?.type === FluidType.CO2FLUID ? obj.flow : null,
        perforation,
        fluid: obj.fluid,
        type: obj.type,
        outsideDiameter: riser?.outsideDiameter,
        insideDiameter: riser?.insideDiameter,
        includeRiser: obj.includeRiser,
        pressureLocation: obj.pressureLocation,
        enthalpyOptionsSource: obj.enthalpyOptionsSource,
        nodalAnalysis: this.includeNodalAnalysis ? obj.nodalAnalysis : null,
        customEnthalpyOptions: obj.enthalpyOptionsSource == 'custom' ? obj.customEnthalpyOptions : null,
        time: shutIn.time,
        pressure: shutIn.pressure.pressure,
        location: shutIn.pressure.location,
        ...commonValues,
      };
    },
    fromFormGroup: (formValue: InjectionOperationForm): InjectionOperation | null => {
      if (!formValue.time) {
        formValue['time'] = 0;
      }
      if (!formValue.time) {
        formValue['pressure'] = 0;
      }
      
      const controls = this.form.formGroup.controls;
      const check = (control, field) => control?.invalid || (control?.touched && this._dataBackup?.[field] === formValue[field]);

      if (check(controls.inletTemperature, 'inletTemperature') || 
          check(controls.wellheadPressure, 'wellheadPressure')) {
        controls.inletTemperature.markAsUntouched();
        controls.wellheadPressure.markAsUntouched();
        return;
      }

      this._dataBackup = formValue;

      if (this.form.formGroup.controls.inletTemperature.value == null) {
        this.form.formGroup.controls.inletTemperature.setValue(this.surfaceTemperature, { emitEvent: false });
        formValue.inletTemperature = this.surfaceTemperature;
        this._dataBackup.inletTemperature = this.surfaceTemperature;
      }

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { fluid, perforation, includeRiser, outsideDiameter, insideDiameter, standardFluidFlow, hydrocarbonFlow, type, pressureLocation, time, pressure, location, enthalpyOptionsSource, nodalAnalysis, customEnthalpyOptions, ...commonValues } = formValue;
      this.optionsCheck(fluid.id);

      this.includeRestartOperation = nodalAnalysis?.includeRestartOperation == true ? true : false;

      switch (true) {
        case fluid?.state.type === FluidType.BRINE || fluid?.state.type === FluidType.STANDARDMUD || fluid?.state.type === FluidType.ADVANCEDMUD || fluid?.state.type === FluidType.CEMENTSLURRY || fluid?.state.type === FluidType.POLYMER:
          return standardFluidFlow ? {
            fluid,
            perforation: {
              id: perforation.id,
              designId: perforation.designId,
              name: perforation.name,
              measuredDepth: perforation.measuredDepth
            },
            includeRiser: includeRiser ?? false,
            riser: this.showRiser ? {
              outsideDiameter,
              insideDiameter
            } : null,
            flow: standardFluidFlow,
            type: OperationType.INJECTION,
            pressureLocation: pressureLocation,
            enthalpyOptionsSource: enthalpyOptionsSource,
            nodalAnalysis: this.includeNodalAnalysis ? nodalAnalysis : null,
            customEnthalpyOptions: enthalpyOptionsSource == 'custom' ? customEnthalpyOptions : null,
            shutIn: {
              time: +time,
              pressure: {
                pressure: +pressure,
                location,
              },
            },
            ...commonValues
          } : null;
        case fluid?.state.type === FluidType.VLE || fluid?.state.type === FluidType.BLACKOIL || fluid?.state.type === FluidType.HYDROCARBON || fluid?.state.type === FluidType.CO2FLUID: {
          let hydFlow;
          if (formValue.nodalAnalysis?.includeRestartOperation && formValue.hydrocarbonFlow == null) {
            hydFlow = {
              co2EosOption: 'GERG2008',
              gasFlowRate: 10,
              gasModel: 'srk',
              gasToOilRatio: 'oilGas',
              multiphaseCorrelation: 'beggsBrill',
              oilFlowRate: 0,
              pipeRoughness: 0.001968,
              type: 'hydrocarbonFlow',
              waterFlowRate: 0
            };
            this.form.formGroup.controls.hydrocarbonFlow.setValue(hydFlow, { emitEvent: false });
          }
          return (hydrocarbonFlow || hydFlow) ? {
            fluid,
            perforation: {
              id: perforation.id,
              designId: perforation.designId,
              name: perforation.name,
              measuredDepth: perforation.measuredDepth
            },
            includeRiser: includeRiser ?? false,
            riser: this.showRiser ? {
              outsideDiameter,
              insideDiameter
            } : null,
            flow: hydrocarbonFlow ?? hydFlow,
            type: OperationType.INJECTION,
            pressureLocation: pressureLocation,
            enthalpyOptionsSource: enthalpyOptionsSource,
            nodalAnalysis: this.includeNodalAnalysis ? nodalAnalysis : null,
            customEnthalpyOptions: enthalpyOptionsSource == 'custom' ? customEnthalpyOptions : null,
            shutIn: {
              time: +time,
              pressure: {
                pressure: +pressure,
                location,
              },
            },
            ...commonValues
          } : null;
        }
        default:
          return null;
      }
    }
  });

  public fluidChangedEvent(e) {
    const state = e.value.state;
    this.fluidChanged.emit({
      hasGasProperty: state.gasComposition != undefined || state.type == 'co2Fluid' || state.type == 'vle',
      hasOilProperty: state.oilApiGravity > 0 || state.type == 'vle',
      hasWaterMudProperty: state.nominalDensity != undefined || state.type == 'blackOil' || state.type == 'vle' || state.type == 'cementSlurry' || state.type == 'polymer'
    });
  }

  public setDefaultSurfaceTemperature() {
    if (this.form.formGroup.controls.inletTemperature.value == null) {
      this.form.formGroup.controls.inletTemperature.setValue(this.surfaceTemperature, { emitEvent: false });
      this._dataBackup.inletTemperature = this.surfaceTemperature;
    }
  }

  ngAfterViewInit(): void {
    // this.isNotShutin();
    const controls = this.form.formGroup.controls;
    const defaultFluid = this.fluids ? this.fluids[0] : {
      id: "0000-0000-0000-0000",
      isInUse: false,
      state: {
        brineType: "seawater",
        nominalDensity: 10,
        plasticViscosity: 1,
        viscosityTemperature: 120,
        name: "10ppg SW",
        type: FluidType.BRINE
      }
    };

    if (!controls.fluid.value) { this.form.formGroup.controls.fluid.setValue(defaultFluid); }
    if (!controls.perforation.value) {
      if (this.perforations) {
        controls.perforation.setValue(this.perforations[0])
      } else {
        controls.perforation.setValue({
          id: "0000-0000-0000-0000",
          designId: '0000-0000-0000-0000',
          name: "Deepest Shoe",
          measuredDepth: 0.0,
          temperature: 250.0
        });
      }
    }

    if (this.form.formGroup.get('enthalpyOptionsSource').value == null) {
      this.form.formGroup.get('enthalpyOptionsSource').setValue('internalDefaults', { emitEvent: false });
      this.enthalpyOptionsSource = 'internalDefaults';
    }

    if (this.defaultRiser.includeRiser && this.form.formGroup.get('includeRiser').value == null) {
      this.form.formGroup.get('includeRiser').setValue(true, { emitEvent: false });
      this.showRiser = true;
      this.checkRiserValues();
    }
  }

    public showJson(data: any) {
      if (!data) {
        return '';
      }
      const perfDefaultTemp = this.perforations.find(p => p.id === this.form.formGroup.controls.perforation.value.id)?.temperature;
      // Don't update indentation
      const formatItem = (item: any) =>
        `Name: ${item.name}
Measured Depth: ${item.measuredDepth?.toFixed(3)} ${this.userUnits.longLengths}
Temperature: ${perfDefaultTemp?.toFixed(3)} ${this.userUnits.temperature}`;

    if (Array.isArray(data)) {
      return data.map(formatItem).join('\n\n');
    }

    return formatItem(data);
  }

  public getPlaceholderText(data: any) {
    const perfExists = this.perforations?.findIndex(p => p.measuredDepth === data?.measuredDepth) >= 0;

    if (data == null) {
      return 'Select';
    }

    return perfExists ? data.name : data.name + ' (local only)';
  }

  public enthalpyOptionsChange(e) {
    this.enthalpyOptionsSource = e;
    this.form.formGroup.get('enthalpyOptionsSource').setValue(e);
  }

  public includeNodalAnalysisChange(e) {
    this.includeNodalAnalysis = e.srcElement.checked;
    if (!this.includeNodalAnalysis) {
      this.form.formGroup.controls.nodalAnalysis.setValue(null);
    }

    this.enableDisableNodalAnalysis();
  }

  private enableDisableNodalAnalysis() {
    if (this.includeNodalAnalysis) {
      this.form.formGroup.controls.nodalAnalysis.enable({ emitEvent: false });
      
      if (this.form.formGroup.controls.wellheadPressure.value == null) {
        this.form.formGroup.controls.wellheadPressure.setValue(0);
      }
    } else {
      this.form.formGroup.controls.nodalAnalysis.disable({ emitEvent: false });
    }
  }

  private optionsCheck(fluidId): void {
    this.showNodal = false;

    const fluidState = this.fluids.find(f => f.id === fluidId)?.state;
    switch (fluidState?.type) {
      case FluidType.VLE:
        break;
      case FluidType.BLACKOIL:
        break;
      case FluidType.CO2FLUID:
        this.showNodal = true;
        break;
      default:
        break
    }
  }

  async ngOnChanges(): Promise<void> {
    this.tempUnit = this.userUnits.temperature;
    this.pressureUnit = unitsLib[this.userUnits.pressure].symbol;
    this.pipeRoughnessUnit = this.userUnits.shortLengths;
    const weightUnit = unitsLib[this.userUnits.linearDensity].symbol;

    const drillPipe = this.drillString['drillPipe'];
    this.selectedLandingString = drillPipe ?
      `Landing String: OD: ${drillPipe.outsideDiameter.toFixed(2)}${this.pipeRoughnessUnit}, Weight: ${drillPipe.weightPerFoot.toFixed(2)}${weightUnit}, ID: ${drillPipe.insideDiameter.toFixed(2)}${this.pipeRoughnessUnit}, Grade: ${drillPipe.drillPipeGrade?.name || 'Steel'}` :
      'Please Select a Landing String';

    this.landingStringMissing = !drillPipe;

    if (this.user.profile) {
      const company = this.user.profile?.organization?.toLowerCase();
      if (company != "altus" && company != "harbour" && company != "baker" && company != "bp" && company != "omv" &&
         company != "fervo" && company != "neptune" && company != "shell" && company != "oxy" && company != "sage" &&
          company != "cop" && company != "ou" && company != "petronas" && company != "inpexau" && company != "te" &&
          company != "ormat") {
        this.fluids = this.fluids.filter(f => f.state.type != 'co2Fluid');
      }
    }

    switch (this.tempUnit) {
      case '°F':
        this.temperatureValidation = { min: -70, max: 1000 };
        break;
      case '°C':
        this.temperatureValidation = { min: -56.66, max: 537.77 };
        break;
      case 'K':
        this.temperatureValidation = { min: 216.48, max: 810.92 };
        break;
    }
    this.form.formGroup.controls.inletTemperature.setValidators([Validators.min(this.temperatureValidation.min), Validators.max(this.temperatureValidation.max), Validators.required]);

    switch (this.userUnits.pressure) {
      case 'psi':
        this.pressureValidation = { min: 0, max: 100000 };
        break;
      case 'bar':
        this.pressureValidation = { min: 0, max: 6894 };
        break;
      case 'KPa':
        this.pressureValidation = { min: 0, max: 689475 };
        break;
      case 'atm':
        this.pressureValidation = { min: 0, max: 6804 };
        break;
    }
    this.form.formGroup.controls.wellheadPressure.setValidators([Validators.min(this.pressureValidation.min), Validators.max(this.pressureValidation.max), Validators.required]);

    setTimeout(() => {
      this.setDefaultSurfaceTemperature();
    }, 50);
  }

  public isShutinChange(e) {
    if (e['srcElement']['checked']) {
      this.isShutin();
    } else {
      this.isNotShutin();
    }
  }

  private isShutin() {
    this.form.formGroup.controls.time.setValue(10, { emitEvent: false });
    this.shutinTimeDisabled = false;
    this.form.formGroup.controls.time.enable();
    this.form.formGroup.controls.pressure.enable({ emitEvent: false });
  }

  private isNotShutin() {
    this.form.formGroup.controls.time.patchValue(0, { emitEvent: false });
    this.form.formGroup.controls.pressure.patchValue(0, { emitEvent: false });
    this.shutinTimeDisabled = true;
    this.form.formGroup.controls.time.disable({ emitEvent: false });
    this.form.formGroup.controls.pressure.disable({ emitEvent: false });
  }

  checkIsShutin(e) {
    if (e === 0) {
      this.isShutInCheck = false;
      this.isNotShutin();
    } else {
      this.isShutInCheck = true;
      this.isShutin();
    }
  }

  public hasRiserChange() {
    this.showRiser = !this.showRiser;
    this.checkRiserValues();
  }

  private checkRiserValues() {
    this.form.formGroup.controls.outsideDiameter
      .setValue((this.form.formGroup.value.outsideDiameter ?? this.defaultRiser?.riser?.outsideDiameter ?? GetShortLengthValueFromInches(21, this.userUnits.shortLengths)), { emitEvent: false });

    this.form.formGroup.controls.insideDiameter
      .setValue((this.form.formGroup.value.insideDiameter ?? this.defaultRiser?.riser?.insideDiameter ?? GetShortLengthValueFromInches(19.75, this.userUnits.shortLengths)));
  }
}
